import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PageLayout from '../../components/layout/PageLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import { supabaseHelpers } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

import PrinterSelector from './components/PrinterSelector';
import MultiFilamentSelector from './components/MultiFilamentSelector';
import ProjectParameters from './components/ProjectParameters';
import RealTimeCostBreakdown from './components/RealTimeCostBreakdown';
import ProfitCalculator from './components/ProfitCalculator';
import ProjectSaver from './components/ProjectSaver';
import ProjectSummaryPanel from './components/ProjectSummaryPanel';

const EnhancedCostCalculator = () => {
  const { success, error } = useToast();
  const publicMode = import.meta.env?.VITE_PUBLIC_MODE === 'true';
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [selectedFilaments, setSelectedFilaments] = useState([]);
  const [selectedMarketplaces, setSelectedMarketplaces] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  
  // Data from database
  const [printers, setPrinters] = useState([]);
  const [filaments, setFilaments] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  const { user } = useAuth();

  const [projectData, setProjectData] = useState({
    name: '',
    duration: '',
    quantity: 1, // peças por plate (em uma única impressão)
    clientMarkup: 65,
    // Enhanced parameters
    printerCost: '',
    investmentReturn: 3,
    dailyUsage: 6,
    repairCost: 5,
    failureRate: 0,
    // Mão de obra em horas
    setupTime: 0,
    setupCostPerHour: 25.0,
    postProcessTime: 0,
    postProcessCostPerHour: 25.0,
    // Categorias adicionais de mão de obra
    designHours: 0,
    designCostPerHour: 25.0,
    approvalHours: 0,
    approvalCostPerHour: 25.0,
    paintingHours: 0,
    paintingCostPerHour: 25.0,
    otherCosts: 0,
    vatPercentage: 0,
    // Energia e desperdício
    electricityRate: 0.75,
    materialWastePct: 0,
    // Embalagens
    packagingEnabled: false,
    packagingUnitPrice: 0,
    // Pricing scenarios
    wholesaleMarkup: 150,
    retailMarkup: 250,
    wholesaleDiscount: 5,
    retailDiscount: 10,
    scenarioQuantity: 100,
    wholesaleVatPercentage: 0,
    retailVatPercentage: 7,
  });

  // Marketplaces handlers
  const handleMarketplaceAdd = (item) => {
    setSelectedMarketplaces(prev => {
      const totalQty = parseInt(projectData?.quantity) || 0;
      const allocated = prev.reduce((sum, m) => sum + (parseInt(m?.quantity) || 0), 0);
      const remaining = Math.max(0, totalQty - allocated);
      const initialQty = parseInt(item?.quantity) || 0;
      const qty = Math.min(initialQty > 0 ? initialQty : 1, remaining);
      if (qty <= 0) return prev; // evita sobrealocação
      return [...prev, { ...item, quantity: qty }];
    });
  };
  const handleMarketplaceUpdate = (index, patch) => {
    setSelectedMarketplaces(prev => prev.map((it, i) => {
      if (i !== index) return it;
      const totalQty = parseInt(projectData?.quantity) || 0;
      const allocatedOther = prev.reduce((sum, m, j) => j === index ? sum : sum + (parseInt(m?.quantity) || 0), 0);
      let desired = patch?.quantity != null ? (parseInt(patch.quantity) || 0) : (it?.quantity || 0);
      const maxForThis = Math.max(0, totalQty - allocatedOther);
      const clampedQty = Math.max(0, Math.min(desired, maxForThis));
      return { ...it, ...patch, quantity: clampedQty };
    }));
  };
  const handleMarketplaceRemove = (index) => {
    setSelectedMarketplaces(prev => prev.filter((_, i) => i !== index));
  };

  const [costs, setCosts] = useState({
    totalMaterial: 0,
    totalElectricity: 0,
    totalDepreciation: 0,
    totalLabor: 0,
    subtotal: 0,
    waste: 0,
    total: 0,
    profit: 0,
    finalPrice: 0,
    perUnit: 0,
    filamentBreakdown: []
  });

  // Complexidade removida do UI/UX e cálculo

  // Load user data
  useEffect(() => {
    // Sempre tenta carregar: quando não houver usuário, usa 'guest' (modo API local)
    loadUserData();
  }, [user]);

  const loadUserData = async () => {
    setLoadingData(true);
    try {
      const effectiveUserId = user?.id || null;
      if (!effectiveUserId && publicMode) {
        // Fallback padrão para exploração sem login
        const sourcePrinters = [
          {
            id: 'bambu-a1-mini-fallback',
            name: 'Bambu A1 Rápida',
            brand: 'Bambu Lab',
            model: 'A1 mini',
            status: 'maintenance',
            hourly_rate: 28,
            power_consumption: 150,
            build_volume_x: 180,
            build_volume_y: 180,
            build_volume_z: 180,
            precision_mm: 0.1,
            max_speed_mm_s: 300,
            has_ams: true,
            nozzle_size: '0.4',
            image_url: 'https://images.unsplash.com/photo-1631016042312-466db558b456'
          }
        ];
        const formattedPrinters = sourcePrinters?.map(printer => ({
          ...printer,
          buildVolume: `${printer?.build_volume_x}×${printer?.build_volume_y}×${printer?.build_volume_z}mm`,
          precision: `${printer?.precision_mm}mm`,
          hourlyRate: printer?.hourly_rate,
          powerConsumption: printer?.power_consumption,
          has_ams: printer?.has_ams,
          nozzle_size: printer?.nozzle_size
        }));
        setPrinters(formattedPrinters);
        setFilaments([
          {
            id: 'pla-default',
            brand: 'Padrão',
            material: 'PLA',
            color: 'branco',
            price_per_kg: 115
          }
        ]);
        return;
      }
      const [printersResult, filamentsResult] = await Promise.all([
        supabaseHelpers?.getUserPrinters(effectiveUserId),
        supabaseHelpers?.getUserFilaments(effectiveUserId)
      ]);

      if (printersResult?.data) {
        let sourcePrinters = Array.isArray(printersResult?.data) ? printersResult?.data : [];

        // Fallback: quando não houver impressoras, injeta dados extraídos da imagem/anexo (Bambu A1)
        if (!sourcePrinters?.length) {
          sourcePrinters = [
            {
              id: 'bambu-a1-mini-fallback',
              name: 'Bambu A1 Rápida',
              brand: 'Bambu Lab',
              model: 'A1 mini',
              status: 'maintenance',
              hourly_rate: 28,
              power_consumption: 150, // 0.15 kW
              build_volume_x: 180,
              build_volume_y: 180,
              build_volume_z: 180,
              precision_mm: 0.1,
              max_speed_mm_s: 300,
              has_ams: true,
              nozzle_size: '0.4',
              image_url: 'https://images.unsplash.com/photo-1631016042312-466db558b456'
            }
          ];
        }

        // Format printer data with Brazilian Portuguese labels
        const formattedPrinters = sourcePrinters?.map(printer => {
          const brandStr = (printer?.brand || printer?.manufacturer || '')?.toLowerCase();
          const modelStr = (printer?.model || '')?.toLowerCase();
          const derivedHasAms = (typeof printer?.has_ams !== 'undefined')
            ? printer?.has_ams
            : (brandStr.includes('bambu') ? true : undefined);
          const derivedNozzle = printer?.nozzle_size || '0.4';
          return {
            ...printer,
            buildVolume: `${printer?.build_volume_x}×${printer?.build_volume_y}×${printer?.build_volume_z}mm`,
            precision: `${printer?.precision_mm}mm`,
            hourlyRate: printer?.hourly_rate,
            powerConsumption: printer?.power_consumption,
            has_ams: derivedHasAms,
            nozzle_size: derivedNozzle
          };
        });
        setPrinters(formattedPrinters);
      }

      if (filamentsResult?.data) {
        setFilaments(filamentsResult?.data);
      }
      } catch (error) {
        console.error('Erro ao carregar dados do usuário:', error);
      } finally {
        setLoadingData(false);
      }
  };

  const handleSidebarToggle = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleProjectDataChange = (field, value) => {
    setProjectData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilamentAdd = (filamentData) => {
    setSelectedFilaments((prev) => [...prev, filamentData]);
  };

  const handleFilamentUpdate = (index, updatedData) => {
    setSelectedFilaments((prev) =>
      prev?.map((filament, i) => i === index ? { ...filament, ...updatedData } : filament)
    );
  };

  const handleFilamentRemove = (index) => {
    setSelectedFilaments((prev) => prev?.filter((_, i) => i !== index));
  };

  const handleFilamentReorder = (fromIndex, toIndex) => {
    // Swap rápido entre posições vizinhas para uma animação mais suave
    setSelectedFilaments((prev) => {
      if (!Array.isArray(prev)) return prev;
      if (fromIndex === toIndex) return prev;
      if (toIndex < 0 || toIndex >= prev.length) return prev;
      const next = [...prev];
      const temp = next[fromIndex];
      next[fromIndex] = next[toIndex];
      next[toIndex] = temp;
      return next;
    });
  };

  const handleFilamentsReorderList = (newOrder) => {
    if (Array.isArray(newOrder)) {
      setSelectedFilaments(newOrder);
    }
  };

  // Removido: etapa de marketplaces. Configuração opcional permanece em Parâmetros.

  // Enhanced realistic cost calculation with advanced parameters
  const calculateRealWorldCosts = useCallback(() => {
    if (!selectedPrinter || selectedFilaments?.length === 0 || !projectData?.duration) {
      return;
    }
    // Indicador rápido de cálculo (evita loop visual prolongado)
    setIsCalculating(true);

    // Cálculo síncrono para evitar loops de "Calculando..."
      const printer = printers?.find((p) => p?.id === selectedPrinter);
      const duration = parseFloat(projectData?.duration);
      const quantity = parseInt(projectData?.quantity) || 1;
      
      // Complexidade removida; usar multiplicador neutro
      const complexityMultiplier = 1.0;

      // Calculate filament costs with precise breakdown, including waste grams
      const filamentBreakdown = selectedFilaments?.map((filament) => {
        const filamentData = filaments?.find((f) => f?.id === filament?.filamentId);
        const weight = parseFloat(filament?.weight || 0);
        const waste = parseFloat(filament?.waste || 0);
        const pricePerKg = (filamentData?.price_per_kg || 0);
        const materialCost = (weight / 1000) * pricePerKg;
        const wasteCost = (waste / 1000) * pricePerKg;
        const cost = materialCost + wasteCost;

        return {
          ...filamentData,
          weight: weight,
          waste: waste,
          cost: cost,
          materialCost,
          wasteCost,
          percentage: 0 // Will be calculated after total
        };
      });

      const materialCostBaseTotal = filamentBreakdown?.reduce((sum, item) => sum + (item?.materialCost || 0), 0) || 0;
      const wasteCostTotal = filamentBreakdown?.reduce((sum, item) => sum + (item?.wasteCost || 0), 0) || 0;
      // Total de material deve considerar o uso informado (por impressão), não multiplicar pela quantidade de peças
      const totalMaterialCost = (materialCostBaseTotal + wasteCostTotal);
      // Desperdício total em gramas por impressão (não multiplicar pela quantidade)
      const totalWasteGrams = (selectedFilaments?.reduce((sum, f) => sum + (parseFloat(f?.waste || 0)), 0) || 0);

      // Brazilian market-based cost calculations with advanced parameters
      
      // 1. Electricity: Realistic consumption including heating, fans, bed
      const powerKwh = (printer?.power_consumption || 220) / 1000;
      const electricityRate = parseFloat(projectData?.electricityRate) || 0.75; // R$/kWh
      const electricityCost = duration * powerKwh * electricityRate; // custo por impressão (não multiplicar por peças)
      
      // 2. Depreciação baseada na impressora selecionada (hora de máquina)
      const machineHourly = parseFloat(printer?.hourly_rate) || 15;
      const totalDepreciationCost = machineHourly * duration; // por impressão
      
      // 3. Enhanced labor cost calculation
      // Mão de obra em horas (não usar minutos)
      const setupTimeHours = (parseFloat(projectData?.setupTime) || 0);
      const setupCostPerHour = parseFloat(projectData?.setupCostPerHour) || 25.0;
      const setupCost = (setupTimeHours * setupCostPerHour * complexityMultiplier); // por impressão

      const postProcessTimeHours = (parseFloat(projectData?.postProcessTime) || 0);
      const postProcessCostPerHour = parseFloat(projectData?.postProcessCostPerHour) || 25.0;
      const postProcessCost = (postProcessTimeHours * postProcessCostPerHour * complexityMultiplier); // por impressão

      // Categorias adicionais de mão de obra
      const designHours = parseFloat(projectData?.designHours) || 0;
      const designCostPerHour = parseFloat(projectData?.designCostPerHour) || setupCostPerHour;
      const designCost = designHours * designCostPerHour;

      const approvalHours = parseFloat(projectData?.approvalHours) || 0;
      const approvalCostPerHour = parseFloat(projectData?.approvalCostPerHour) || setupCostPerHour;
      const approvalCost = approvalHours * approvalCostPerHour;

      const paintingHours = parseFloat(projectData?.paintingHours) || 0;
      const paintingCostPerHour = parseFloat(projectData?.paintingCostPerHour) || postProcessCostPerHour;
      const paintingCost = paintingHours * paintingCostPerHour;

      const totalLaborCost = setupCost + postProcessCost + designCost + approvalCost + paintingCost;
      
      // 4. Other costs
      const otherCosts = parseFloat(projectData?.otherCosts) || 0;

      // Manutenção, falhas e ROI (parâmetros opcionais)
      const repairCostPct = parseFloat(projectData?.repairCost) || 15; // % sobre depreciação
      const maintenanceCost = totalDepreciationCost * (repairCostPct / 100);
      const failureRatePct = parseFloat(projectData?.failureRate) || 0; // % sobre material
      const failureCost = totalMaterialCost * (failureRatePct / 100);
      const roiCost = parseFloat(projectData?.investmentReturn) || 0; // R$ por impressão

      // Embalagens: preço unitário multiplicado pela quantidade de peças (sempre ativo)
      const packagingUnitPrice = parseFloat(projectData?.packagingUnitPrice) || 0;
      const packagingCostTotal = (packagingUnitPrice * quantity);

      const subtotal = totalMaterialCost + electricityCost + totalDepreciationCost + totalLaborCost + otherCosts + maintenanceCost + failureCost + roiCost + packagingCostTotal;

      // 5. Waste calculation (realistic for 3D printing)
      // Waste is accounted via per-filament grams; expose wasteCost for UI only
      const wasteAmount = wasteCostTotal; // custo de desperdício já incluso no material (por impressão)
      const totalBeforeTax = subtotal; // não somar desperdício novamente

      // 6. VAT/Tax calculation
      const vatPercentage = parseFloat(projectData?.vatPercentage) || 0;
      const vatAmount = subtotal * (vatPercentage / 100);
      const totalAfterTax = subtotal + vatAmount;

      // 7. Profit margin (Brazilian market standard)
      const profitPercentage = parseFloat(projectData?.clientMarkup) || 65;
      const profit = totalAfterTax * (profitPercentage / 100);
      const finalPriceWithoutMarketplace = totalAfterTax + profit; // preço base sem marketplace
      let finalPrice = finalPriceWithoutMarketplace; // sobrescrito se marketplace selecionado

      // 8. Taxas de Marketplace
      let marketplaceFeesPercent = 0;
      let marketplaceFeesFixed = 0;
      let marketplaceFeesTotal = 0;
      let marketplaceBreakdown = [];

      if (selectedMarketplaces?.length > 0) {
        // Calcular preço por marketplace usando dados cadastrados
        marketplaceBreakdown = selectedMarketplaces?.map((sel) => {
          const percent = Math.max(0, Math.min(95, parseFloat(sel?.fee_percent || 0)));
          const p = percent / 100;
          const fixedPerItem = Math.max(0, parseFloat(sel?.fixed_fee_per_unit || 0));
          const itemsPerTransaction = Math.max(0, parseInt(sel?.quantity || 0));
          const fixedTotal = fixedPerItem * itemsPerTransaction; // taxa fixa por transação multiplicada por itens/transação
          const base = finalPrice + fixedTotal;
          const denom = (1 - p) > 0.0001 ? (1 - p) : 1;
          const totalPrice = base / denom;
          const perUnitPrice = totalPrice / quantity;
          const percentFeeAmount = totalPrice * p;
          const fixedFeeAmount = fixedTotal;
          const unitMargin = (sel?.targetUnitPrice != null) ? (sel?.targetUnitPrice - perUnitPrice) : null;
          const unitMarginPercent = (sel?.targetUnitPrice != null && perUnitPrice > 0) ? ((sel?.targetUnitPrice - perUnitPrice) / perUnitPrice) * 100 : null;

          return {
            marketplaceId: sel?.marketplaceId,
            name: sel?.name,
            quantity: sel?.quantity || 0,
            percent,
            fixed_per_unit: fixedPerItem,
            perUnitPrice,
            totalPrice,
            targetUnitPrice: sel?.targetUnitPrice,
            unitMargin,
            unitMarginPercent,
            percentFeeAmount,
            fixedFeeAmount,
          };
        });

        // Recomendar preço final como o maior entre marketplaces selecionados
        const recommended = marketplaceBreakdown?.reduce((acc, cur) => cur?.totalPrice > (acc?.totalPrice || 0) ? cur : acc, {});
        if (recommended?.totalPrice) {
          finalPrice = recommended?.totalPrice;
        }
        // Resumo das taxas baseado no marketplace recomendado
        marketplaceFeesPercent = recommended?.percentFeeAmount || 0;
        marketplaceFeesFixed = recommended?.fixedFeeAmount || 0;
        marketplaceFeesTotal = marketplaceFeesPercent + marketplaceFeesFixed;
      }

      // Update filament breakdown with percentages
      const updatedFilamentBreakdown = filamentBreakdown?.map((item) => ({
        ...item,
        percentage: totalMaterialCost > 0 ? ((item?.cost || 0) / totalMaterialCost) * 100 : 0
      }));

      // Pricing scenarios: atacado/varejo, descontos e impostos por canal
      const unitBaseCost = totalAfterTax / quantity; // custo por unidade sem lucro
      const wholesaleMarkup = parseFloat(projectData?.wholesaleMarkup) || 150;
      const retailMarkup = parseFloat(projectData?.retailMarkup) || 250;
      const wholesaleSuggestedUnit = unitBaseCost * (1 + wholesaleMarkup / 100);
      const retailSuggestedUnit = unitBaseCost * (1 + retailMarkup / 100);
      const scenarioQty = parseInt(projectData?.scenarioQuantity) || 100;
      const wholesaleDiscount = parseFloat(projectData?.wholesaleDiscount) || 5;
      const retailDiscount = parseFloat(projectData?.retailDiscount) || 10;
      const wholesaleFinalUnit = wholesaleSuggestedUnit * (1 - wholesaleDiscount / 100);
      const retailFinalUnit = retailSuggestedUnit * (1 - retailDiscount / 100);
      const wholesaleVatPct = parseFloat(projectData?.wholesaleVatPercentage) || (parseFloat(projectData?.vatPercentage) || 0);
      const retailVatPct = parseFloat(projectData?.retailVatPercentage) || 7;
      const wholesaleTaxesUnit = wholesaleFinalUnit * (wholesaleVatPct / 100);
      const retailTaxesUnit = retailFinalUnit * (retailVatPct / 100);
      const wholesaleSales = wholesaleFinalUnit * scenarioQty;
      const retailSales = retailFinalUnit * scenarioQty;
      const wholesaleCostsTotal = unitBaseCost * scenarioQty;
      const retailCostsTotal = unitBaseCost * scenarioQty;
      const wholesaleNetProfit = wholesaleSales - wholesaleCostsTotal - (wholesaleTaxesUnit * scenarioQty);
      const retailNetProfit = retailSales - retailCostsTotal - (retailTaxesUnit * scenarioQty);
      const wholesaleNetProfitPct = wholesaleSales > 0 ? (wholesaleNetProfit / wholesaleSales) * 100 : 0;
      const retailNetProfitPct = retailSales > 0 ? (retailNetProfit / retailSales) * 100 : 0;
      const diffPts = retailNetProfitPct - wholesaleNetProfitPct;
      const diffValue = retailNetProfit - wholesaleNetProfit;

      // Marketplace por unidade (se disponível do recomendado)
      const recommended = (marketplaceBreakdown || [])?.reduce((acc, cur) => cur?.totalPrice > (acc?.totalPrice || 0) ? cur : acc, {});
      const marketplaceUnitPercentFee = recommended?.percentFeeAmount ? (recommended?.percentFeeAmount / quantity) : 0;
      const marketplaceUnitFixedFee = recommended?.fixedFeeAmount ? (recommended?.fixedFeeAmount / quantity) : 0;
      const marketplaceUnitPrice = recommended?.perUnitPrice || 0;

      setCosts({
        totalMaterial: totalMaterialCost,
        totalElectricity: electricityCost,
        totalDepreciation: totalDepreciationCost,
        totalLabor: totalLaborCost,
        designCost,
        approvalCost,
        paintingCost,
        setupCost: setupCost,
        postProcessCost: postProcessCost,
        otherCosts: otherCosts,
        packagingCost: packagingCostTotal,
        roiCost,
        maintenanceCost,
        failureCost,
        subtotal: subtotal,
        waste: wasteAmount,
        totalWasteGrams,
        vatAmount: vatAmount,
        total: totalAfterTax,
        profit: profit,
        finalPrice: finalPrice,
        perUnit: finalPrice / quantity,
        filamentBreakdown: updatedFilamentBreakdown,
        // Advanced breakdown simplificado
        machineHourlyRate: machineHourly,
        setupTimeHours: setupTimeHours,
        postProcessTimeHours: postProcessTimeHours,
        // Marketplace resumo
        marketplaceFeesPercent,
        marketplaceFeesFixed,
        marketplaceFeesTotal,
        marketplaceBreakdown,
        // Comparativo sem marketplace
        finalPriceWithoutMarketplace,
        perUnitWithoutMarketplace: finalPriceWithoutMarketplace / quantity,
        // Pricing scenarios
        pricing: {
          unitBaseCost,
          wholesale: {
            markupPercent: wholesaleMarkup,
            suggestedUnitPrice: wholesaleSuggestedUnit,
            finalUnitPrice: wholesaleFinalUnit,
            discountPercent: wholesaleDiscount,
            taxesPercent: wholesaleVatPct,
            taxesUnit: wholesaleTaxesUnit,
            quantity: scenarioQty,
            salesValue: wholesaleSales,
            costsTotal: wholesaleCostsTotal,
            netProfitTotal: wholesaleNetProfit,
            netProfitPercent: wholesaleNetProfitPct,
          },
          retail: {
            markupPercent: retailMarkup,
            suggestedUnitPrice: retailSuggestedUnit,
            finalUnitPrice: retailFinalUnit,
            discountPercent: retailDiscount,
            taxesPercent: retailVatPct,
            taxesUnit: retailTaxesUnit,
            quantity: scenarioQty,
            salesValue: retailSales,
            costsTotal: retailCostsTotal,
            netProfitTotal: retailNetProfit,
            netProfitPercent: retailNetProfitPct,
          },
          marketplace: {
            unitPercentFee: marketplaceUnitPercentFee,
            unitFixedFee: marketplaceUnitFixedFee,
            unitTotalFees: marketplaceUnitPercentFee + marketplaceUnitFixedFee,
            unitMarketplacePrice: marketplaceUnitPrice,
          },
          diff: {
            points: diffPts,
            value: diffValue,
          }
        }
      });

      console.log('💰 Cálculo avançado realizado:', {
        material: totalMaterialCost,
        electricity: electricityCost,
        depreciation: totalDepreciationCost,
        labor: totalLaborCost,
        packaging: packagingCostTotal,
        total: finalPrice,
        quantity: quantity
      });

      // Finaliza o estado de cálculo imediatamente
      setIsCalculating(false);
  }, [
    selectedPrinter,
    selectedFilaments,
    selectedMarketplaces,
    printers,
    filaments,
    projectData?.duration,
    projectData?.quantity,
    projectData?.clientMarkup,
    projectData?.vatPercentage,
    projectData?.otherCosts,
    projectData?.setupTime,
    projectData?.setupCostPerHour,
    projectData?.postProcessTime,
    projectData?.postProcessCostPerHour,
    // Extended pricing dependencies
    projectData?.wholesaleMarkup,
    projectData?.retailMarkup,
    projectData?.wholesaleDiscount,
    projectData?.retailDiscount,
    projectData?.wholesaleVatPercentage,
    projectData?.retailVatPercentage,
    projectData?.scenarioQuantity,
    // Failure and maintenance
    projectData?.failureRate,
    projectData?.repairCost,
    projectData?.investmentReturn,
  ]);

  // Auto-calculate when parameters change
  useEffect(() => {
    if (selectedPrinter && selectedFilaments?.length > 0 && projectData?.duration) {
      calculateRealWorldCosts();
    }
  }, [calculateRealWorldCosts]);

  // Prefill printerCost based on selected printer (hourly_rate heuristic or stored cost)
  useEffect(() => {
    if (!selectedPrinter) return;
    const p = printers?.find(pr => pr?.id === selectedPrinter);
    if (!p) return;
    const derivedCost = (p?.purchase_cost && parseFloat(p?.purchase_cost)) || ((p?.hourly_rate || 15) * 200);
    setProjectData(prev => ({
      ...prev,
      printerCost: derivedCost
    }));
  }, [selectedPrinter, printers]);

  const handleSaveProject = async (projectName) => {
    if (!user?.id) return;

    try {
      const projectToSave = {
        user_id: user?.id,
        printer_id: selectedPrinter,
        name: projectName || projectData?.name || 'Projeto Sem Nome',
        description: `Projeto calculado em ${new Date()?.toLocaleDateString('pt-BR')} às ${new Date()?.toLocaleTimeString('pt-BR')}`,
        // complexidade removida
        quantity: projectData?.quantity,
        estimated_duration_hours: parseFloat(projectData?.duration),
        // material_waste_percentage removido; uso de desperdício por gramas
        client_markup_percentage: projectData?.clientMarkup,
        // Enhanced fields
        printer_purchase_cost: parseFloat(projectData?.printerCost) || 0,
        expected_printer_lifetime_years: parseInt(projectData?.investmentReturn) || 3,
        daily_usage_hours: parseFloat(projectData?.dailyUsage) || 6,
        repair_cost_percentage: parseFloat(projectData?.repairCost) || 5,
        setup_time_minutes: Math.round((parseFloat(projectData?.setupTime) || 0) * 60),
        setup_cost_per_hour: parseFloat(projectData?.setupCostPerHour) || 25,
        postprocessing_time_minutes: Math.round((parseFloat(projectData?.postProcessTime) || 0) * 60),
        postprocessing_cost_per_hour: parseFloat(projectData?.postProcessCostPerHour) || 25,
        other_costs: parseFloat(projectData?.otherCosts) || 0,
        vat_percentage: parseFloat(projectData?.vatPercentage) || 0,
        // Marketplace params
        // Marketplace manual fees removidas
        // Cost breakdown
        total_material_cost: costs?.totalMaterial,
        total_electricity_cost: costs?.totalElectricity,
        total_depreciation_cost: costs?.totalDepreciation,
        total_labor_cost: costs?.totalLabor,
        subtotal_cost: costs?.subtotal,
        waste_cost: costs?.waste,
        total_cost: costs?.total,
        profit_amount: costs?.profit,
        final_price: costs?.finalPrice,
        price_per_unit: costs?.perUnit
      };

      const { data: project, error } = await supabaseHelpers?.createProjectWithAdvancedCosting(projectToSave);
      
      if (error) {
        error('Erro ao salvar projeto: ' + (error?.message || ''));
        return;
      }

      // Save filament relationships
      for (const filament of selectedFilaments) {
        const filamentData = filaments?.find(f => f?.id === filament?.filamentId);
        if (filamentData && project?.id) {
          await supabaseHelpers?.addProjectFilament({
            project_id: project?.id,
            filament_id: filament?.filamentId,
            weight_grams: parseFloat(filament?.weight) || 0,
            waste_grams: parseFloat(filament?.waste) || 0,
            cost_amount: ((parseFloat(filament?.weight) || 0) + (parseFloat(filament?.waste) || 0)) / 1000 * (filamentData?.price_per_kg || 0),
            percentage_of_total: 0
          });

          // Atualiza estoque do filamento com base no uso neste projeto
          const qty = parseInt(projectData?.quantity) || 1;
          const usedGrams = ((parseFloat(filament?.weight) || 0) + (parseFloat(filament?.waste) || 0)) * qty;
          const currentStock = parseFloat(filamentData?.stock_grams ?? 0);
          if (!Number.isNaN(currentStock) && currentStock > 0) {
            const newStock = Math.max(0, currentStock - usedGrams);
            try {
              await supabaseHelpers?.updateFilament(filamentData?.id, { stock_grams: newStock });
            } catch (e) {
              console.warn('Falha ao atualizar estoque do filamento', filamentData?.id, e);
            }
          }
        }
      }

      // Save marketplace relationships (for metrics and linkage)
      if (Array.isArray(selectedMarketplaces) && selectedMarketplaces?.length > 0 && project?.id) {
        for (const sel of selectedMarketplaces) {
          await supabaseHelpers?.addProjectMarketplace({
            project_id: project?.id,
            marketplace_id: sel?.marketplaceId,
            quantity: parseInt(sel?.quantity) || 0,
            applied_fee_percent: parseFloat(sel?.fee_percent) || 0,
            applied_fixed_fee_per_unit: parseFloat(sel?.fixed_fee_per_unit) || 0
          });
        }
      }

      success('Projeto salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar projeto:', error);
      error('Erro ao salvar projeto');
    }
  };

  const handleExportQuote = () => {
    const selectedPrinterData = printers?.find((p) => p?.id === selectedPrinter);
    const quote = {
      projectName: projectData?.name || 'Orçamento FioGo',
      printer: selectedPrinterData?.name,
      totalCost: costs?.finalPrice,
      breakdown: costs,
      timestamp: new Date()?.toLocaleString('pt-BR'),
      company: 'FioGo - Soluções em Impressão 3D'
    };

    // Create detailed downloadable content in Portuguese
    const content = `
=== ORÇAMENTO FIOGO - IMPRESSÃO 3D ===
Data: ${quote?.timestamp}

INFORMAÇÕES DO PROJETO:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• Nome do Projeto: ${quote?.projectName}
• Impressora: ${quote?.printer}
• Quantidade: ${projectData?.quantity} unidade(s)
• Tempo de Impressão: ${projectData?.duration}h


BREAKDOWN DETALHADO DE CUSTOS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• Material (Filamento): R$ ${costs?.totalMaterial?.toFixed(2)}
• Energia Elétrica: R$ ${costs?.totalElectricity?.toFixed(2)}
• Mão de Obra: R$ ${costs?.totalLabor?.toFixed(2)}
• Depreciação Equipamento: R$ ${costs?.totalDepreciation?.toFixed(2)}
• Desperdício de Material: R$ ${costs?.waste?.toFixed(2)} (Total: ${costs?.totalWasteGrams?.toFixed(1)}g)
• Lucro (${projectData?.clientMarkup}%): R$ ${costs?.profit?.toFixed(2)}

FILAMENTOS UTILIZADOS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${costs?.filamentBreakdown?.map(f => 
  `• ${f?.brand} ${f?.material} ${f?.color}: ${f?.weight}g - R$ ${f?.cost?.toFixed(2)}`
)?.join('\n')}

TOTAIS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• Custo Total: R$ ${costs?.total?.toFixed(2)}
• Preço Final: R$ ${costs?.finalPrice?.toFixed(2)}
• Por Unidade: R$ ${costs?.perUnit?.toFixed(2)}

${quote?.company}
app.fiogo.com.br
Orçamento válido por 30 dias.
`;

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orcamento-fiogo-${Date.now()}.txt`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const steps = [
    { id: 1, label: 'Impressora', icon: 'Printer', completed: !!selectedPrinter },
    { id: 2, label: 'Filamentos', icon: 'Filament', completed: selectedFilaments?.length > 0 },
    { id: 3, label: 'Parâmetros', icon: 'Settings', completed: !!projectData?.duration },
    { id: 4, label: 'Resultados', icon: 'Calculator', completed: costs?.total > 0 }
  ];

  const isFormValid = selectedPrinter && selectedFilaments?.length > 0 && projectData?.duration;

  if (loadingData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-4 animate-fade-in">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-lg text-foreground">Carregando dados do sistema...</span>
        </div>
      </div>
    );
  }

  return (
    <PageLayout
      title="Calculadora Profissional 3D"
      subtitle="Precificação realista baseada no mercado brasileiro de impressão 3D"
      iconName="Calculator"
    >
      {/* Modern Page Header */}
      <motion.div
            className="mb-8 animate-slide-up"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}>
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-5">
                {/* Header visuals are handled by PageLayout */}
              </div>

              {/* Input de nome removido do header para evitar duplicação; use o painel Resumo do Projeto */}
            </div>

            {/* Enhanced Progress Steps */}
            <div className="flex items-center space-x-6 mb-8 overflow-x-auto pb-2 pl-2">
              {steps?.map((step, index) =>
                <div key={step?.id} className="flex items-center space-x-3 min-w-fit">
                  <motion.button
                    onClick={() => setActiveStep(step?.id)}
                    className={`
                      flex items-center space-x-3 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-300
                      ${activeStep === step?.id ?
                        'bg-primary text-primary-foreground shadow-elevation-2 scale-105' :
                        step?.completed ?
                          'bg-success/10 text-success hover:bg-success/20 border border-success/20' : 'bg-card border border-border text-muted-foreground hover:bg-muted hover:border-primary/30'}
                    `}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <div className={`
                      w-8 h-8 rounded-lg flex items-center justify-center
                      ${activeStep === step?.id ? 'bg-transparent' : step?.completed ? 'bg-success/20' : 'bg-muted/50'}
                    `}>
                      <Icon
                        name={step?.completed && activeStep !== step?.id ? 'CheckCircle' : step?.icon}
                        size={18}
                        variant={
                          (step?.completed && activeStep !== step?.id) 
                            ? 'lucide' 
                            : ((step?.icon === 'Printer' || step?.icon === 'Filament') ? 'masked' : 'lucide')
                        }
                        className={
                          activeStep === step?.id 
                            ? 'text-primary-foreground' 
                            : (step?.completed ? 'text-success' : 'text-muted-foreground')
                        }
                      />
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="font-semibold">{step?.label}</span>
                      <span className="text-xs opacity-75">
                        {step?.id === 1 && 'Escolher equipamento'}
                        {step?.id === 2 && 'Selecionar materiais'}
                        {step?.id === 3 && 'Configurar projeto'}
                        {step?.id === 4 && 'Ver orçamento'}
                      </span>
                    </div>
                  </motion.button>
                  {index < steps?.length - 1 &&
                    <div className={`w-8 h-0.5 transition-colors duration-300 ${
                      step?.completed ? 'bg-success' : 'bg-border'
                    }`} />
                  }
                </div>
              )}
            </div>
          </motion.div>

          {/* Resumo do Projeto movido para a coluna direita, abaixo de Salvar Projeto */}

          {/* Main Content - Enhanced Step-based Layout */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
            {/* Left Column - Configuration Steps */}
            <div className="xl:col-span-3 space-y-8">
              <AnimatePresence mode="wait">
                {activeStep === 1 &&
                  <motion.div
                    key="printer-step"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 30 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}>
                    <PrinterSelector
                      selectedPrinter={selectedPrinter}
                      onPrinterChange={setSelectedPrinter}
                      printers={printers}
                      onNext={() => setActiveStep(2)} />
                  </motion.div>
                }

                {activeStep === 2 &&
                  <motion.div
                    key="filament-step"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 30 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}>
                    <MultiFilamentSelector
                      selectedFilaments={selectedFilaments}
                      filaments={filaments}
                      projectQuantity={projectData?.quantity}
                      onFilamentAdd={handleFilamentAdd}
                      onFilamentUpdate={handleFilamentUpdate}
                      onFilamentRemove={handleFilamentRemove}
                      onFilamentReorder={handleFilamentReorder}
                      onFilamentsReorder={handleFilamentsReorderList}
                      onNext={() => setActiveStep(3)} />
                  </motion.div>
                }

                {activeStep === 3 &&
                  <motion.div
                    key="parameters-step"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 30 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}>
                    <ProjectParameters
                      projectData={projectData}
                      onDataChange={handleProjectDataChange}
                      selectedPrinter={selectedPrinter}
                      selectedFilaments={selectedFilaments}
                      selectedMarketplaces={selectedMarketplaces}
                      onMarketplaceAdd={handleMarketplaceAdd}
                      onMarketplaceUpdate={handleMarketplaceUpdate}
                      onMarketplaceRemove={handleMarketplaceRemove}
                      defaultTransactionItems={projectData?.quantity}
                      onNext={() => setActiveStep(4)} />
                  </motion.div>
                }

                {activeStep === 4 &&
                  <motion.div
                    key="results-step"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 30 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}>
                    <div className="space-y-8">
                      <RealTimeCostBreakdown
                        costs={costs}
                        isCalculating={isCalculating}
                        projectData={projectData} />
                      
                      <ProfitCalculator
                        projectData={projectData}
                        costs={costs}
                        onDataChange={handleProjectDataChange} />
                    </div>
                  </motion.div>
                }
              </AnimatePresence>

              {/* Enhanced Navigation Buttons */}
              <motion.div 
                className="flex items-center justify-between pt-8 border-t border-border"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Button
                  variant="outline"
                  onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
                  disabled={activeStep === 1}
                  size="xl"
                  className="flex items-center space-x-2"
                >
                  <Icon name="ChevronLeft" size={18} />
                  <span>Etapa Anterior</span>
                </Button>

                {activeStep < 4 ? (
                  <Button
                    onClick={() => setActiveStep(Math.min(4, activeStep + 1))}
                    disabled={
                      (activeStep === 1 && !selectedPrinter) ||
                      (activeStep === 2 && selectedFilaments?.length === 0) ||
                      (activeStep === 3 && !projectData?.duration)
                    }
                    size="xl"
                    className="flex items-center space-x-2"
                  >
                    <span>Próxima Etapa</span>
                    <Icon name="ChevronRight" size={18} />
                  </Button>
                ) : (
                  <div className="flex items-center space-x-4">
                    <Button
                      variant="outline"
                      onClick={handleExportQuote}
                      disabled={!isFormValid}
                      size="xl"
                      className="flex items-center space-x-2"
                    >
                      <Icon name="Download" size={18} />
                      <span>Exportar Orçamento</span>
                    </Button>
                    
                    <Button
                      onClick={() => handleSaveProject(projectData?.name || 'Novo Projeto')}
                      disabled={!isFormValid}
                      size="xl"
                      className="flex items-center space-x-2 bg-gradient-to-r from-primary to-secondary"
                    >
                      <Icon name="Save" size={18} />
                      <span>Salvar Projeto</span>
                    </Button>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Right Column - Live Preview & Project Info */}
            <motion.div 
              className="space-y-6"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <ProjectSaver
                projectData={projectData}
                onDataChange={handleProjectDataChange}
                costs={costs}
                onSave={handleSaveProject} />

              {/* Resumo do Projeto abaixo de Salvar Projeto */}
              <ProjectSummaryPanel
                selectedPrinter={selectedPrinter}
                printers={printers}
                selectedFilaments={selectedFilaments}
                filaments={filaments}
                projectData={projectData}
                costs={costs}
                isCalculating={isCalculating}
              />
            </motion.div>
          </div>
    </PageLayout>
  );
};

export default EnhancedCostCalculator;